import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Star, MousePointerClick } from "lucide-react";
import { RevealLines } from "./Motion";
import { scrollToId } from "./SmoothScroll";

const HeroScene = lazy(() => import("./HeroScene"));

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] w-full overflow-hidden bg-[#06060d]">
      {/* 3D layer */}
      <div className="absolute inset-0 z-0">
        <Suspense fallback={<div className="h-full w-full bg-[#06060d]" />}>
          <HeroScene />
        </Suspense>
      </div>

      {/* legibility overlays */}
      <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_center,rgba(6,6,13,0.72)_0%,rgba(6,6,13,0.35)_45%,rgba(6,6,13,0.85)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-40 bg-gradient-to-b from-transparent to-[#06060d]" />

      {/* content */}
      <div className="relative z-20 mx-auto flex min-h-[100svh] max-w-[1360px] flex-col items-center justify-center px-5 py-32 text-center md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/80 backdrop-blur-md"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#28c840]" />
          Webdesign · Google Ads · SEO
        </motion.div>

        <h1 className="max-w-4xl font-display text-[13.5vw] leading-[0.9] text-white sm:text-6xl md:text-7xl lg:text-8xl">
          <RevealLines lines={["Websites,"]} delay={0.35} />
          <RevealLines lines={["die Kunden"]} delay={0.46} />
          <span className="block bg-gradient-to-r from-[#6b78ff] via-[#2c3bff] to-[#8f9bff] bg-clip-text text-transparent">
            <RevealLines lines={["gewinnen."]} delay={0.57} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          <strong className="text-white">IYM. Im Your Man.</strong> Deine Full-Service-Agentur für
          Webdesign, Entwicklung und performante Google-Ads-Kampagnen. Wir bauen digitale Auftritte,
          die messbar verkaufen.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToId("kontakt")}
            className="btn-fill flex items-center gap-2 rounded-full bg-[#2c3bff] px-7 py-4 font-display text-sm uppercase tracking-wide text-white"
          >
            Kostenloses Angebot
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToId("arbeiten")}
            className="rounded-full border border-white/25 px-7 py-4 font-display text-sm uppercase tracking-wide text-white transition-colors hover:bg-white hover:text-ink"
          >
            Arbeiten ansehen
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3"
        >
          <div className="flex items-center gap-1.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-[#febc2e] text-[#febc2e]" />
            ))}
            <span className="ml-1 font-mono text-xs text-white/70">4.9/5 Kundenbewertung</span>
          </div>
          <div className="hidden h-5 w-px bg-white/20 sm:block" />
          <span className="font-mono text-xs text-white/70">50+ Projekte live</span>
        </motion.div>
      </div>

      {/* scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="pointer-events-none absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50"
      >
        <MousePointerClick className="h-3.5 w-3.5" />
        Bewege die Maus · Scroll
      </motion.div>
    </section>
  );
}
